import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Field, useFormikContext } from 'formik';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import { Checkbox, Chip, ListItemText, ListSubheader, Select } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import orange from '@material-ui/core/colors/orange';
import unionBy from 'lodash/unionBy';
import { connect } from 'react-redux';
import { TextField } from 'formik-material-ui';
import Typography from '@material-ui/core/Typography';
import { QUESTIONS } from '../constants';
import InputLabel from '../../../Form/InputLabel';
import { fetchUserBracketLibraries } from '../../../../reducers/bracketLibraries';
import { fetchCustomBracketSetsAction } from '../../../../reducers/customBracketSets';
import Teeth from '../../../../pages/CustomBracketSets/Teeth';

const useStyles = makeStyles(theme => ({
  lowStockChip: {
    backgroundColor: orange[500],
    color: '#fff'
  },
  customSetBracketList: {
    width: 500
  },
  items: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 10,
    '& > .custom-set-item': {
      width: 'auto'
    },
    '& > li:not(.custom-set-item)': {
      gridColumn: 'span 1'
    },
    '@media (min-width: 1500px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
      '& > li:not(.custom-set-item)': {
        gridColumn: 'span 2'
      }
    }
  }
}));

const extractCombinedValue = (values, bracketLibrariesName, customBracketSetName) => {
  const { [bracketLibrariesName]: value, [customBracketSetName]: chosenCustomSetId } = values;

  const fieldValue = [];

  if (values.bracket_libraries && value.length) {
    value.forEach(item => {
      fieldValue.push(`bracket_library-${item}`);
    });
  } else if (chosenCustomSetId) {
    fieldValue.push(`custom_set-${chosenCustomSetId}`);
  }

  return fieldValue;
};

const BracketLibrariesField = ({
  name,
  doctorId,
  fetched,
  fetching,
  fetch,
  items,
  customBracketSetAttributeName,
  fetchCustomBracketSets,
  fetchedCustomBracketSets,
  fetchingCustomBracketSets,
  customBracketSets,
  customBracketSetsParams,
  resetValueOnFetch
}) => {
  const classes = useStyles();
  const { values, setValues, setFieldError, isValidating, errors } = useFormikContext();

  const { [name]: value, chosen_bracket_libraries: chosenBracketLibraries } = values;

  const fieldValue = extractCombinedValue(values, name, customBracketSetAttributeName);

  const bracketLibraries = useMemo(() => {
    return [...unionBy(items, chosenBracketLibraries || [], 'id')];
  }, [chosenBracketLibraries, items]);

  const updateValue = updatedValue => {
    setValues({
      ...values,
      ...updatedValue
    });
  };

  // fetch bracket libraries
  // reset value when fetching for bracket libraries
  useEffect(() => {
    if (!fetched && !fetching && doctorId) {
      if (resetValueOnFetch && value && value.length) {
        setValues({
          ...values,
          [name]: [],
          [customBracketSetAttributeName]: null
        });
      }
      fetch();
    }
  }, [
    fetched,
    fetching,
    fetch,
    doctorId,
    setValues,
    values,
    name,
    value,
    resetValueOnFetch,
    customBracketSetAttributeName
  ]);

  // fetch custom bracket sets
  // reset value when fetching for custom bracket sets
  useEffect(() => {
    if (
      fetchingCustomBracketSets ||
      (fetchedCustomBracketSets && customBracketSetsParams.assigned_to_user_id === doctorId)
    ) {
      return;
    }
    if (resetValueOnFetch && values[customBracketSetAttributeName]) {
      setValues({
        ...values,
        [customBracketSetAttributeName]: null
      });
    }
    fetchCustomBracketSets({ assigned_to_user_id: doctorId });
  }, [
    fetchCustomBracketSets,
    fetchingCustomBracketSets,
    customBracketSets,
    doctorId,
    fetchedCustomBracketSets,
    customBracketSetsParams,
    setValues,
    values,
    resetValueOnFetch,
    customBracketSetAttributeName
  ]);

  // Active validation for both bracket libraries and custom sets
  useEffect(() => {
    if (isValidating) {
      return () => {};
    }
    const timeout = setTimeout(() => {
      if (
        (bracketLibraries.length > 0 || customBracketSets.length > 0) &&
        (!fieldValue || fieldValue.length === 0)
      ) {
        if (errors[name]) {
          return;
        }
        setFieldError(name, 'Please select at least one bracket library or custom set.');
      } else {
        if (!errors[name]) {
          return;
        }
        setFieldError(name, '');
      }
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [isValidating, name, fieldValue, bracketLibraries, customBracketSets, setFieldError, errors]);

  return (
    <FormControl variant="outlined" fullWidth>
      <InputLabel id={`${name}-label`}>
        {fetching || fetchingCustomBracketSets ? 'Loading Brackets' : QUESTIONS[name]}
      </InputLabel>
      <Field
        style={{ display: 'none' }}
        name={customBracketSetAttributeName}
        component={TextField}
      />
      <Field
        MenuProps={{
          MenuListProps: { className: customBracketSets.length > 0 ? classes.items : '' }
        }}
        disabled={items.length === 0}
        labelId={`${name}-label`}
        required
        name={name}
        multiple
        component={Select}
        value={fieldValue}
        renderValue={selected => {
          if (selected.length === 1 && selected[0].startsWith('custom_set-')) {
            const customSetId = selected[0].split('-')[1];
            const customSet = customBracketSets.find(item => item.id === Number(customSetId));
            return customSet ? customSet.name : '';
          }
          const selectedLibraries = bracketLibraries.filter(item =>
            selected.includes(`bracket_library-${item.id}`)
          );
          return selectedLibraries.map(item => item.label).join(', ');
        }}
        onChange={event => {
          const newValues = event.target.value;
          // filter invalid click on group header
          if (newValues.includes(undefined)) {
            return;
          }

          if (newValues.length === 0) {
            // if no values selected, reset both fields
            setValues({
              ...values,
              [name]: [],
              [customBracketSetAttributeName]: null
            });
            return;
          }
          if (newValues.length > value.length) {
            // get difference between new and old values
            const newValue = newValues.filter(item => !fieldValue.includes(item))[0];

            // get new value b
            const [type, id] = newValue.split('-');
            // support multiple selection for bracket libraries and single selection for custom sets
            if (type === 'custom_set') {
              updateValue({
                [name]: [],
                [customBracketSetAttributeName]: Number(id)
              });
            } else {
              updateValue({
                [name]: [...value, Number(id)],
                [customBracketSetAttributeName]: null
              });
            }
          } else {
            updateValue({
              [name]: newValues.map(item => item.split('-')[1]),
              [customBracketSetAttributeName]: null
            });
          }
        }}
        labelWidth={65}
      >
        {customBracketSets.length > 0 && <ListSubheader disableSticky>Custom Sets</ListSubheader>}
        {customBracketSets.length > 0 &&
          customBracketSets.map(item => {
            const itemValue = `custom_set-${item.id}`;
            return (
              <MenuItem key={`custom-set-${item.id}`} value={itemValue} className="custom-set-item">
                <Checkbox checked={fieldValue.includes(itemValue)} />
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                >
                  <Typography variant="subtitle1">
                    {item.name}&nbsp;
                    {item.is_pending_creation && (
                      <Chip label="Pending" size="small" color="secondary" />
                    )}
                  </Typography>
                  <Teeth
                    size="small"
                    data={item.teeth}
                    BracketListProps={{ className: classes.customSetBracketList }}
                  />
                </div>
              </MenuItem>
            );
          })}
        {customBracketSets.length > 0 && bracketLibraries.length > 0 && (
          <ListSubheader disableSticky>Individual Bracket Libraries</ListSubheader>
        )}

        {bracketLibraries.map(item => {
          const itemValue = `bracket_library-${item.id}`;
          return (
            <MenuItem key={`bracket-library-${item.id}`} value={itemValue}>
              <Checkbox checked={fieldValue.includes(itemValue)} />
              <ListItemText primary={item.label} />
              {item.low_stock && (
                <Chip size="small" label="Low Inventory" className={classes.lowStockChip} />
              )}
            </MenuItem>
          );
        })}
      </Field>
    </FormControl>
  );
};

BracketLibrariesField.propTypes = {
  name: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  items: PropTypes.array,
  doctorId: PropTypes.number,
  fetched: PropTypes.bool.isRequired,
  fetching: PropTypes.bool.isRequired,
  fetch: PropTypes.func.isRequired,
  resetValueOnFetch: PropTypes.bool.isRequired,
  customBracketSetAttributeName: PropTypes.string.isRequired,
  fetchCustomBracketSets: PropTypes.func.isRequired,
  fetchedCustomBracketSets: PropTypes.bool.isRequired,
  fetchingCustomBracketSets: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  customBracketSets: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  customBracketSetsParams: PropTypes.object.isRequired
};

BracketLibrariesField.defaultProps = {
  items: [],
  doctorId: null
};

export default connect(
  (state, { organizationId, withCustomBracketSets, doctorId, originalValue }) => {
    const { bracketLibraries } = state;
    const userState = (bracketLibraries.users[doctorId] &&
      bracketLibraries.users[doctorId][organizationId]) || {
      items: [],
      fetched: false,
      fetching: false
    };
    return {
      items: userState.items.filter(
        item => !item.replaced_by_id || (originalValue || []).includes(item.id)
      ),
      fetching: userState.fetching,
      fetched: userState.fetched,
      customBracketSets: withCustomBracketSets ? state.customBracketSets.list.data : [],
      fetchedCustomBracketSets: withCustomBracketSets
        ? state.customBracketSets.list.initialized
        : true,
      fetchingCustomBracketSets: state.customBracketSets.list.loading,
      customBracketSetsParams: state.customBracketSets.parameters
    };
  },
  (dispatch, { organizationId, doctorId }) => {
    return {
      fetch: () => dispatch(fetchUserBracketLibraries(organizationId, doctorId)),
      fetchCustomBracketSets: params =>
        dispatch(fetchCustomBracketSetsAction(organizationId, params))
    };
  }
)(BracketLibrariesField);
